// Shadow-AI relationship graph API client
// (mirrors services/firewall/src/services/discoveryGraph.ts)

export type NodeType = 'identity' | 'agent' | 'destination' | 'mcp';
export type RiskLevel = 'sanctioned' | 'tolerated' | 'unsanctioned' | 'high_risk';

export interface GraphNode {
  id: string;
  label: string;
  type: NodeType;
  risk?: RiskLevel;
  managed: boolean;
  source: string;
  count: number;
  provider?: string;
  /** Discovery row id for agent nodes — the handle for renaming them. */
  discId?: string;
}

/** Give a discovered agent an operator-chosen name (empty string clears it). */
export async function renameDiscoveredAgent(apiUrl: string, discId: string, displayName: string): Promise<void> {
  const res = await fetch(`${apiUrl}/admin/discovery/agents/${encodeURIComponent(discId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ display_name: displayName }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || body.error || `Rename failed (${res.status})`);
  }
}

export interface GraphLink {
  source: string;
  target: string;
  kind: 'operates' | 'egress';
  count: number;
}

export interface DiscoveryGraph {
  nodes: GraphNode[];
  links: GraphLink[];
  available: { sources: string[]; risks: string[]; types: NodeType[] };
}

export interface GraphFilters {
  sources?: string[];
  risks?: RiskLevel[];
  managed?: 'all' | 'shadow' | 'managed';
  types?: NodeType[];
  /** ISO timestamp — only nodes observed at/after this time are kept (undated nodes are exempt). */
  since?: string;
}

export async function getDiscoveryGraph(apiUrl: string, filters: GraphFilters = {}): Promise<DiscoveryGraph> {
  const params = new URLSearchParams();
  if (filters.sources?.length) params.set('sources', filters.sources.join(','));
  if (filters.risks?.length) params.set('risks', filters.risks.join(','));
  if (filters.types?.length) params.set('types', filters.types.join(','));
  if (filters.managed && filters.managed !== 'all') params.set('managed', filters.managed);
  if (filters.since) params.set('since', filters.since);
  const qs = params.toString();
  const res = await fetch(`${apiUrl}/admin/discovery/graph${qs ? `?${qs}` : ''}`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || body.error || `Request failed (${res.status})`);
  }
  return res.json();
}
